import { useEffect, useRef, useState } from "react";
import { Clock, HelpCircle, Bot, ShieldAlert, CalendarClock, LineChart, ArrowRight, Check, Minus, ClipboardPaste, Search, Rocket, CircleDashed } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/landing/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HOME_FAQ } from "@/seo/faq";

export const Heading = ({ eyebrow, children, sub, className = "max-w-3xl" }) => (
  <Reveal className={className}>
    <p className="eyebrow">{eyebrow}</p>
    <h2 className="mt-3 text-[2rem] sm:text-[2.75rem] leading-[1.04]">{children}</h2>
    {sub && <p className="mt-3 text-[15px] sm:text-base text-muted-foreground leading-relaxed">{sub}</p>}
  </Reveal>
);

const PAINS = [
  [Clock, "Manual SEO eats hours", "A title, 13 tags and attributes take 20–30 minutes per listing.", "Batch fixes for up to 50 listings, each reviewed by you."],
  [HelpCircle, "Guesswork, no data", "Most edits are made on instinct, with no record of why.", "A Health Score per listing and a reason for every change."],
  [Bot, "Generic AI doesn't know Etsy", "Chat tools ignore 13 tags, 20-character tags and 140-character titles.", "Every fix is checked against Etsy's rules before you push."],
  [ShieldAlert, "Bulk edits feel risky", "One bad bulk edit can hit the listings that pay the bills.", "Bestseller Shield, a backup on every push, one-click undo."],
  [CalendarClock, "Seasonal shifts go unnoticed", "Holiday demand moves faster than a big catalog.", "Seasonal alerts ~90 days ahead are in the pipeline."],
  [LineChart, "No proof anything worked", "Without a comparison, nobody knows if an edit helped.", "Proof compares edited listings with untouched ones."],
];

export const NextStep = ({ to, onClick, children, testid }) => {
  const cls = "mt-6 inline-flex min-h-[44px] items-center gap-1.5 text-sm text-[#5EE6D0] underline-offset-4 hover:underline";
  return to
    ? <Link to={to} data-testid={testid} className={cls}>{children}<ArrowRight className="h-4 w-4" /></Link>
    : <button type="button" onClick={onClick} data-testid={testid} className={cls}>{children}<ArrowRight className="h-4 w-4" /></button>;
};

export const PainPoints = () => (
  <section data-testid="pain-points" className="mx-auto max-w-7xl px-5 py-12 sm:px-8 sm:py-14">
    <Heading eyebrow="Etsy SEO for busy sellers" sub="Big catalogs need an Etsy SEO tool that's systematic, safe and provable. What usually gets in the way, and the fix:">
      You didn't open an Etsy shop to become <span className="italic text-gold-grad">an Etsy SEO expert.</span>
    </Heading>
    <p className="mt-6 text-xs text-muted-foreground sm:hidden">Swipe for all six →</p>
    <Reveal className="hairline mt-3 flex snap-x snap-mandatory gap-px overflow-x-auto overscroll-x-contain rounded-3xl border bg-white/[0.07] [scrollbar-width:none] sm:mt-8 sm:grid sm:grid-cols-2 lg:grid-cols-3">
      {PAINS.map(([Icon, title, pain, fix], i) => (
        <div key={title} data-testid={`pain-card-${i}`} className="min-w-[82%] snap-start bg-[#0A0B14] px-5 py-4 sm:min-w-0">
          <h3 className="flex items-center gap-2.5 font-display text-lg sm:text-xl leading-tight"><Icon className="h-4 w-4 shrink-0 text-[#E9C98B]" />{title}</h3>
          <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{pain}</p>
          <p className="mt-1.5 flex gap-2 text-sm text-[#5EE6D0]"><ArrowRight className="mt-0.5 h-4 w-4 shrink-0" />{fix}</p>
        </div>
      ))}
    </Reveal>
    <p data-testid="pain-source-note" className="mt-4 text-xs text-muted-foreground">Sources: Etsy's listing rules (13 tags, 20 characters per tag, 140-character titles) and Etsy's 2025 title guidance. Time per listing is a typical estimate, not a measurement.</p>
    <NextStep to="/tools/etsy-seo-checker" testid="pain-next-step">Score one listing free with the Etsy SEO checker</NextStep>
  </section>
);

const SAMPLE = {
  before: { score: 38, title: "Beautiful Unique Perfect Gift Brass Candle Holder Candle Holder Vintage Decor Gift For Her Best Gift",
    tags: ["candle holder", "gift", "vintage", "brass", "decor", "gift for her", "unique"], attrs: [["Material", "—"], ["Color", "—"], ["Style", "—"]] },
  after: { score: 86, title: "Vintage Brass Candlestick Holder, Mid Century Taper Candle Holder for Mantel or Table",
    tags: ["brass candlestick", "vintage candle holder", "mid century decor", "taper candle holder", "brass home decor", "mantel decor", "table centerpiece", "vintage brass", "retro candlestick", "housewarming gift", "gold candle holder", "dining table decor", "boho candle holder"],
    attrs: [["Material", "Brass"], ["Color", "Gold"], ["Style", "Mid-century"]] },
};

export const BeforeAfterSample = () => {
  const [state, setState] = useState("before");
  const [onScreen, setOnScreen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { threshold: 0.25 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!onScreen || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => setState((s) => (s === "before" ? "after" : "before")), 4200);
    return () => clearInterval(t);
  }, [onScreen]);
  return (
    <div ref={ref} data-testid="before-after">
      <Reveal className="glass hairline-gold rounded-3xl border p-5 sm:p-7">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span data-testid="before-after-sample-badge" className="rounded-full border border-[#E9C98B]/40 bg-[#E9C98B]/10 px-3 py-1 text-xs uppercase tracking-wider text-[#F3DDAE]">Sample · before / after</span>
          <div role="tablist" aria-label="Before or after" className="flex rounded-full border border-white/10 p-1">
            {["before", "after"].map((k) => (
              <button key={k} role="tab" aria-selected={state === k} onClick={() => setState(k)} data-testid={`before-after-toggle-${k}`}
                className={`min-h-[44px] rounded-full px-5 text-sm capitalize transition-colors ${state === k ? "bg-white/10 text-foreground" : "text-muted-foreground"}`}>{k}</button>
            ))}
          </div>
        </div>
        {/* Both states share one grid cell, so the box height never changes; only opacity/transform animate. */}
        <div className="mt-6 grid" style={{ contain: "layout" }}>
          {["before", "after"].map((k) => <BeforeAfterPane key={k} k={k} active={state === k} />)}
        </div>
        <p className="mt-5 text-xs text-muted-foreground">A sample listing, not a real shop: a stuffed title with 7 tags becomes a clean title with all 13 tags and filled attributes.</p>
      </Reveal>
    </div>
  );
};

const BeforeAfterPane = ({ k, active }) => {
  const d = SAMPLE[k];
  const tid = (s) => (active ? s : undefined);
  return (
    <div aria-hidden={!active} data-testid={`before-after-${k}`}
      className={`grid gap-5 [grid-area:1/1] transition-[opacity,transform] duration-500 ease-out ${active ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 translate-y-1"}`}>
      <div className="flex items-center gap-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Health Score</p>
        <p data-testid={tid("before-after-score")} className={`font-display text-6xl ${k === "after" ? "text-[#5EE6D0]" : "text-rose-200"}`}>{d.score}</p>
      </div>
      <div className="space-y-4">
        <p className="text-lg leading-snug" data-testid={tid("before-after-title")}>{d.title}</p>
        <div className="flex flex-wrap gap-1.5">
          {d.tags.map((t) => <span key={t} className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-xs">{t}</span>)}
          <span data-testid={tid("before-after-tag-count")} className="px-2 py-1 text-xs text-muted-foreground">{d.tags.length} of 13 tags</span>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          {d.attrs.map(([a, v]) => <span key={a}><span className="text-muted-foreground">{a}:</span> {v}</span>)}
        </div>
      </div>
    </div>
  );
};

export const FreeToolsStrip = () => (
  <section data-testid="free-tools-strip" className="mx-auto max-w-7xl px-5 py-12 sm:px-8 sm:py-14">
    <Heading eyebrow="Free Etsy tools">Free Etsy SEO tools: try it on <span className="italic text-gold-grad">one listing, free.</span></Heading>
    <div className="mt-8 grid gap-3 md:grid-cols-3">
      {[["etsy-tag-generator", "Etsy Tag Generator", "13 Etsy-valid tag suggestions from your product description."],
        ["etsy-title-generator", "Etsy Title Generator", "Three clear titles of 140 characters or fewer, plus shop title ideas."],
        ["etsy-seo-checker", "Etsy SEO Checker", "A 0–100 score with fixes for any title, tags and description."]].map(([slug, name, blurb]) => (
        <Link key={slug} to={`/tools/${slug}`} data-testid={`free-tools-strip-${slug}`}
          className="group rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#E9C98B]/35">
          <span className="font-display text-xl text-foreground">{name}</span>
          <span className="mt-1.5 block text-sm leading-relaxed text-muted-foreground">{blurb}</span>
          <span className="mt-3 block text-sm text-[#5EE6D0] group-hover:underline underline-offset-4">Use it free →</span>
        </Link>
      ))}
    </div>
    <p className="mt-5 text-sm text-muted-foreground">No sign-up, no email. <Link to="/tools" data-testid="free-tools-strip-all" className="text-[#5EE6D0] underline underline-offset-4">All free tools</Link></p>
  </section>
);

export const ResearchCard = () => (
  <Reveal data-testid="research-tools" className="glass hairline rounded-2xl border p-6 lg:col-span-2">
    <h3 className="font-display text-2xl leading-tight">Works with eRank, Marmalead <span className="italic text-gold-grad">or your own research</span></h3>
    <p data-testid="research-tools-copy" className="mt-2 text-[15px] text-muted-foreground leading-relaxed">
      Already pay for a keyword tool? Paste your Etsy keyword research into “Your keywords” on one listing or a whole batch, and AetherTags turns it into Etsy-ready titles, 13 tags, attributes and descriptions, then pushes them safely. No research tool? Our AI engine handles it end to end.
    </p>
    <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
      {[[Search, "Research in your tool"], [ClipboardPaste, "Paste into “Your keywords”"], [Rocket, "Review, approve, push"]].map(([Icon, t]) => (
        <li key={t} className="inline-flex items-center gap-2"><Icon className="h-4 w-4 text-[#5EE6D0]" />{t}</li>
      ))}
    </ul>
    <p data-testid="research-tools-note" className="mt-3 text-xs text-muted-foreground">AetherTags is not affiliated with, endorsed by or connected to eRank or Marmalead. Names are used only to describe compatibility.</p>
  </Reveal>
);

const ROWS = [
  ["Connects to your shop", "Yes, official Etsy API", "No, copy and paste", "Yes, inside Etsy"],
  ["Scores every listing", "Yes, 0–100 per listing", "No", "Tips, no per-listing score"],
  ["All 13 tags + attributes + description", "Yes", "Text only, unchecked", "Titles (tag/attribute suggestions in testing)"],
  ["Etsy-rule checks", "Yes, before every push", "No", "For the title"],
  ["Approval before changes", "Yes, always", "No, you edit by hand", "Yes"],
  ["Bestseller Shield", "Yes", "No", "No"],
  ["One-click undo", "Yes", "No", "Edit back manually"],
  ["Before/after proof", "Yes, edited vs untouched", "No", "Stats only, no comparison"],
  ["Works with your keyword research", "Yes, “Your keywords”", "If you paste it in", "No"],
];
const Cell = ({ v, strong }) => {
  const icon = v.startsWith("Yes") ? <Check className={`mt-0.5 h-4 w-4 shrink-0 ${strong ? "text-[#5EE6D0]" : "opacity-60"}`} />
    : v.startsWith("No") ? <Minus className="mt-0.5 h-4 w-4 shrink-0 opacity-50" />
    : <CircleDashed className="mt-0.5 h-4 w-4 shrink-0 opacity-50" />;
  return (
    <td className={`px-4 py-2.5 text-sm ${strong ? "text-foreground" : "text-muted-foreground"}`}>
      <span className="inline-flex items-start gap-2">{icon}{v}</span>
    </td>
  );
};

export const Comparison = () => (
  <section data-testid="why-aethertags" className="mx-auto max-w-7xl px-5 py-12 sm:px-8 sm:py-14">
    <Heading eyebrow="Why AetherTags" sub="How this Etsy SEO tool compares with generic AI chat and Etsy's built-in title tool:">
      AI chat writes text. <span className="italic text-gold-grad">AetherTags runs the whole Etsy SEO loop.</span>
    </Heading>
    <p className="mt-6 text-xs text-muted-foreground sm:hidden">Swipe the table sideways to compare →</p>
    <Reveal data-testid="comparison" className="glass hairline-gold mt-3 overflow-x-auto rounded-3xl border sm:mt-8">
      <table className="w-full min-w-[640px] text-left" data-testid="comparison-table">
        <thead className="text-xs uppercase tracking-wider text-muted-foreground">
          <tr className="border-b border-white/10"><th className="sticky left-0 z-10 bg-[#0C0D17] px-4 py-3.5 font-medium sm:static sm:bg-transparent">Capability</th><th className="px-4 py-3.5 font-medium text-[#F3DDAE]">AetherTags</th><th className="px-4 py-3.5 font-medium">Generic AI chat</th><th className="px-4 py-3.5 font-medium">Etsy's built-in title tool</th></tr>
        </thead>
        <tbody>{ROWS.map(([cap, a, b, c]) => (
          <tr key={cap} className="border-t border-white/[0.06]"><th scope="row" className="sticky left-0 z-10 max-w-[150px] bg-[#0C0D17] px-4 py-2.5 text-sm font-normal shadow-[8px_0_12px_-10px_rgba(0,0,0,0.9)] sm:static sm:max-w-none sm:bg-transparent sm:shadow-none">{cap}</th><Cell v={a} strong /><Cell v={b} /><Cell v={c} /></tr>
        ))}</tbody>
      </table>
    </Reveal>
    <p data-testid="comparison-line" className="mt-6 font-display text-2xl sm:text-3xl leading-snug">Etsy gives you a suggestion. AetherTags gives you <span className="italic text-gold-grad">the strategy behind it — and the proof.</span></p>
    <p className="mt-3 text-xs text-muted-foreground">Based on publicly available information as of October 2026. Categories, not specific products.</p>
    <NextStep to="/tools/etsy-title-generator" testid="why-next-step">Draft a clean title free with the Etsy title generator</NextStep>
  </section>
);

export const HomeFaq = () => (
  <div data-testid="home-faq">
    <Heading eyebrow="Etsy SEO FAQ">Etsy SEO tool questions, <span className="italic text-gold-grad">answered.</span></Heading>
    <Accordion type="single" collapsible defaultValue="q0" className="mt-6">
      {HOME_FAQ.map(([q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="group border-white/10" data-testid={`home-faq-item-${i}`}>
          <AccordionTrigger className="min-h-[44px] py-3 text-left font-display text-lg sm:text-xl font-normal hover:no-underline" data-testid={`home-faq-trigger-${i}`}>{q}</AccordionTrigger>
          <AccordionContent forceMount className="text-[15px] leading-relaxed text-muted-foreground group-data-[state=closed]:hidden">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);
